import { useEffect, useRef } from 'react';

/**
 * WaveDisrupt – adds a subtle mouse-driven disruption to the
 * waveDrift body background pattern.
 *
 * How it works:
 *  - Tracks the cursor position relative to the viewport.
 *  - Smoothly lerps toward a target offset (X & Y) proportional to
 *    how far the mouse is from center.
 *  - Applies the offset as a CSS custom property `--wave-disrupt`
 *    which is picked up by the body's backgroundPosition via an
 *    inline style written directly to document.body.
 *
 * Max disruption: ±24px on X, ±14px on Y.
 * Lerp speed: 0.06 (smooth, organic feel).
 */
const WaveDisrupt = () => {
  const rafRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 }); // normalised 0-1
  const current = useRef({ x: 0, y: 0 });   // current px offset
  const target = useRef({ x: 0, y: 0 });    // target px offset
  const isHovering = useRef(false);

  const MAX_X = 24;
  const MAX_Y = 14;
  const LERP = 0.06;

  useEffect(() => {
    const onMouseMove = (e) => {
      isHovering.current = true;
      mouse.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };

    const onMouseLeave = () => {
      isHovering.current = false;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseleave', onMouseLeave);

    const tick = () => {
      // Centre-relative: -1 to +1
      const nx = (mouse.current.x - 0.5) * 2;
      const ny = (mouse.current.y - 0.5) * 2;

      if (isHovering.current) {
        target.current.x = nx * MAX_X;
        target.current.y = ny * MAX_Y;
      } else {
        // Drift back to zero when mouse leaves
        target.current.x = 0;
        target.current.y = 0;
      }

      // Lerp current toward target
      current.current.x += (target.current.x - current.current.x) * LERP;
      current.current.y += (target.current.y - current.current.y) * LERP;

      // Apply to body's background-position-x / y as an additive offset.
      // The waveDrift keyframe already animates backgroundPositionX via
      // the shorthand; we override backgroundPosition here so the browser
      // naturally sums both (the CSS animation runs on backgroundPosition
      // while we write the *same* property with !important won't work –
      // instead we use a CSS variable via a style tag approach).
      document.body.style.setProperty(
        '--wave-ox',
        `${current.current.x.toFixed(2)}px`
      );
      document.body.style.setProperty(
        '--wave-oy',
        `${current.current.y.toFixed(2)}px`
      );

      rafRef.current = requestAnimationFrame(tick);
    };

    // Inject a small <style> so the keyframe can read the CSS vars.
    // We pause the CSS animation and drive position manually via JS.
    const styleEl = document.createElement('style');
    styleEl.id = 'wave-disrupt-style';
    styleEl.textContent = `
      @keyframes waveDriftEnhanced {
        0%   { background-position: calc(0px   + var(--wave-ox, 0px)) calc(0px + var(--wave-oy, 0px)); }
        100% { background-position: calc(100px + var(--wave-ox, 0px)) calc(0px + var(--wave-oy, 0px)); }
      }
      body {
        --wave-ox: 0px;
        --wave-oy: 0px;
        animation-name: waveDriftEnhanced !important;
        animation-duration: 8s !important;
        animation-timing-function: linear !important;
        animation-iteration-count: infinite !important;
      }
    `;
    document.head.appendChild(styleEl);

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
      cancelAnimationFrame(rafRef.current);
      document.getElementById('wave-disrupt-style')?.remove();
      document.body.style.removeProperty('--wave-ox');
      document.body.style.removeProperty('--wave-oy');
    };
  }, []);

  return null;
};

export default WaveDisrupt;
